import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, CheckCircle2, XCircle, Send, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

/**
 * Full-page "Check your email" landing shown after registration or a
 * 403 EMAIL_NOT_VERIFIED login. User must open the inbox, click the
 * link, and complete the /verify-email flow — there is no in-page
 * bypass. Resend-verification is rate-limited at the backend (3/hr
 * per IP + 60s cooldown per user).
 */
export const CheckEmailPage = () => {
  const { resendVerification } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const email = params.get('email') || location.state?.email || '';
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email) {
      toast.error('No email on record — please re-register.');
      return;
    }
    setSending(true);
    try {
      await resendVerification(email);
      toast.success('If that account exists, we re-sent the link.');
      setCooldown(60);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error('Too many resend requests. Try again in an hour.');
      } else {
        toast.error('Could not resend — please try again later.');
      }
    } finally {
      setSending(false);
    }
  };

  const obfuscated = useMemo(() => {
    if (!email || !email.includes('@')) return email;
    const [local, domain] = email.split('@');
    const visible = local.length <= 2 ? local[0] : local.slice(0, 2);
    return `${visible}${'•'.repeat(Math.max(1, local.length - 2))}@${domain}`;
  }, [email]);

  return (
    <div
      data-testid="check-email-page"
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: '#05050A' }}
    >
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-10">
          <Shield className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.2em]">Government Shield</span>
        </Link>

        <div className="relative rounded-2xl border border-slate-800 bg-[#0F1115] p-8 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-500/10 text-blue-400 mb-6">
              <Mail className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-black tracking-tight text-slate-50 mb-3">
              Check your email
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed mb-1">
              We sent a verification link to
            </p>
            <p className="text-sm font-mono text-slate-200 mb-6 break-all" data-testid="check-email-address">
              {obfuscated || 'your email address'}
            </p>
            <p className="text-sm text-slate-500 leading-relaxed mb-8">
              Click the link within <b className="text-slate-300">1 hour</b> to activate your account.
              If you don't see it, check your spam folder.
            </p>

            <Button
              data-testid="resend-verification-btn"
              onClick={handleResend}
              disabled={sending || cooldown > 0}
              className="w-full"
              variant="secondary"
            >
              <Send className="w-4 h-4 mr-2" />
              {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? 'Sending…' : 'Resend verification email'}
            </Button>
          </div>
        </div>

        <div className="mt-6 text-xs text-slate-500 leading-relaxed">
          Already verified?{' '}
          <Link to="/login" className="text-blue-400 hover:underline" data-testid="to-login-link">
            Log in →
          </Link>
        </div>
      </div>
    </div>
  );
};


/**
 * Target of the verification link embedded in the email. Reads the
 * `email` + `token` query params, POSTs to the backend, and routes
 * to /login (or back to /check-email on failure) with an appropriate
 * toast.
 */
export const VerifyEmailPage = () => {
  const { verifyEmail } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | ok | expired | invalid | missing
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const email = params.get('email');
    const token = params.get('token');
    if (!email || !token) {
      setStatus('missing');
      return;
    }
    (async () => {
      try {
        await verifyEmail(email, token);
        setStatus('ok');
        toast.success('Email verified — you can log in now.');
        setTimeout(() => navigate('/login'), 1400);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail === 'TOKEN_EXPIRED' || err?.response?.status === 410) {
          setStatus('expired');
        } else {
          setStatus('invalid');
        }
      }
    })();
  }, [params, verifyEmail, navigate]);

  const retryEmail = params.get('email') || '';

  return (
    <div
      data-testid="verify-email-page"
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: '#05050A' }}
    >
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-10">
          <Shield className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.2em]">Government Shield</span>
        </Link>

        <div className="relative rounded-2xl border border-slate-800 bg-[#0F1115] p-8">
          {status === 'verifying' && (
            <div data-testid="verify-status-loading">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-500/10 mb-6">
                <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-50 mb-2">Verifying…</h1>
              <p className="text-sm text-slate-500">Hang on — just a moment.</p>
            </div>
          )}
          {status === 'ok' && (
            <div data-testid="verify-status-ok">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 mb-6">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-slate-50 mb-2">Email verified</h1>
              <p className="text-sm text-slate-500 mb-6">Redirecting you to the login page…</p>
            </div>
          )}
          {status === 'expired' && (
            <div data-testid="verify-status-expired">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500/10 text-amber-400 mb-6">
                <XCircle className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-slate-50 mb-2">Link expired</h1>
              <p className="text-sm text-slate-500 mb-6">
                Verification links are good for 1 hour. Request a fresh one below.
              </p>
              <Button
                onClick={() => navigate(`/check-email?email=${encodeURIComponent(retryEmail)}`)}
                className="w-full"
                data-testid="verify-goto-resend-btn"
              >
                Get a new link
              </Button>
            </div>
          )}
          {(status === 'invalid' || status === 'missing') && (
            <div data-testid="verify-status-invalid">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-red-500/10 text-red-400 mb-6">
                <XCircle className="w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-slate-50 mb-2">Invalid link</h1>
              <p className="text-sm text-slate-500 mb-6">
                This verification link isn't valid. It may have already been used, or the URL was tampered with in transit.
              </p>
              <Button
                onClick={() => navigate(`/check-email?email=${encodeURIComponent(retryEmail)}`)}
                className="w-full"
                data-testid="verify-goto-resend-btn"
              >
                Request a new link
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
